import axios from 'axios';

/**
 * Constitution VI mandates that the UI never surface raw API errors.
 * Every hunting-zones surface funnels backend failures through this
 * helper so the displayed copy stays consistent and never leaks backend
 * internals (DB constraint names, stack excerpts, internal paths).
 *
 * Feature 003 reshaped the zones API to a read-only + auto-commit-sync
 * surface, so the feature-001 conflict dialogs (upload / commit / bulk)
 * are gone — the only mutating call is the Notion sync trigger, open to any
 * authenticated user and failing with 401 (session expired) or 5xx (infra).
 * The returned `kind` decides the UI shape (toast vs full sign-in redirect).
 */
export type ZonesApiErrorPresentation =
  | { kind: 'redirect'; message: string }
  | { kind: 'toast'; message: string };

interface MapOptions {
  /**
   * Which call produced the error. `sync` = the Notion sync trigger;
   * `outcome` = the terminal-outcome fetch; `list` = the read-only
   * dashboard list. Used only to tune copy; the default `unknown`
   * yields the generic message.
   */
  source: 'sync' | 'outcome' | 'list' | 'unknown';
}

const DEFAULT_GENERIC: ZonesApiErrorPresentation = {
  kind: 'toast',
  message: "Something went wrong on our end. We've been notified.",
};

/**
 * Extract the HTTP status from whatever an HttpService call throws.
 *
 * `HttpService.request` does NOT rethrow `AxiosError`s — its interceptor
 * rewraps every error response into a plain `Error("HTTP <status>: ...")`
 * carrying a numeric `status` property, and transport failures into a
 * status-less `Error`. Branching on `axios.isAxiosError` is therefore blind
 * in production, so classification must read the carried `status` first and
 * only fall back to the AxiosError response status for back-compat (e.g. a
 * raw AxiosError that somehow bypasses the rewrap, or unit tests).
 *
 * Returns `undefined` when no status is available (transport failure, bare
 * throw) — callers treat that as "unexpected".
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null) {
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') return status;
  }
  if (axios.isAxiosError(error) && error.response) {
    return error.response.status;
  }
  return undefined;
}

/**
 * Map an arbitrary error thrown by an HttpService call into a typed
 * presentation. Inputs with no resolvable HTTP status fall back to the
 * generic message so the UI never crashes on a thrown string or undefined.
 */
export function mapZonesApiError(
  error: unknown,
  _options: MapOptions = { source: 'unknown' },
): ZonesApiErrorPresentation {
  const status = getErrorStatus(error);
  if (status === undefined) {
    return DEFAULT_GENERIC;
  }

  return mapByStatus(status);
}

/**
 * Expected, user-actionable HTTP statuses for the zones surface. These map
 * to a clear toast/redirect the user can act on, so they are NOT reported
 * to Bugsnag (doing so would drown real infra failures in auth/validation
 * noise). Everything else — 5xx, network failures, thrown non-Axios values
 * — is unexpected and worth a Bugsnag report.
 */
const EXPECTED_CLIENT_STATUSES: ReadonlySet<number> = new Set([400, 401, 404, 409]);

/**
 * Whether an error thrown by a zones HttpService call is unexpected (and so
 * worth reporting to Bugsnag). True for 5xx / unhandled statuses, transport
 * failures with no response, and non-Axios throws; false for the expected
 * client statuses above.
 */
export function isUnexpectedZonesError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === undefined) {
    return true;
  }
  return !EXPECTED_CLIENT_STATUSES.has(status);
}

function mapByStatus(status: number): ZonesApiErrorPresentation {
  switch (status) {
    case 400:
      // Constitution VI — do NOT interpolate the backend's message
      // verbatim. The detail still reaches Bugsnag via the AxiosError.
      return {
        kind: 'toast',
        message: "We couldn't process that request. Try again in a moment.",
      };
    case 401:
      return { kind: 'redirect', message: 'Please sign in again to continue.' };
    case 404:
      return { kind: 'toast', message: 'That sync job no longer exists. Start a new sync.' };
    case 409:
      // The outcome endpoint returns 409 while the job is still in
      // progress. Callers normally gate on a terminal status before
      // fetching the outcome, so this is a fallback message only.
      return {
        kind: 'toast',
        message: 'The sync is still in progress. Wait for it to finish before viewing the outcome.',
      };
    default:
      return DEFAULT_GENERIC;
  }
}
